import { getHttpClient } from '@safe-minds-ai/api';
import type { AuthSession } from '@safe-minds-ai/api';
import type { FlagRecord } from '@safe-minds-ai/shared-dtos';
import type { UiFlagCategory } from '@safe-minds-ai/utils';

export interface HouseholdSummary {
  id: string;
  name: string;
  flaggedPrompts: number;
  onlineChildren: number;
  lastActive: string;
}

export interface GuidanceItem {
  id: string;
  childName: string;
  topic: string;
  status: 'Awaiting Review' | 'Scheduled' | 'Completed';
}

export interface ControlPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
}

export interface ChildProfile {
  id: string;
  nickname: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName: string;
  age: number;
  grade?: number | null;
  flaggedPrompts?: number;
  status?: string;
  timeLeftMinutes?: number;
  alerts?: number;
  lastSessionAt?: string | null;
}

export interface ParentProfile {
  id: string;
  firstName: string;
  lastName: string;
  subscriptionType: string;
  contactNumber?: string | null;
  organization?: string | null;
  children: ChildProfile[];
  createdAt: string;
  updatedAt: string;
  user?: { email: string };
  plan?: {
    id: string;
    name: string;
    priceMonth?: number | null;
    features?: PolicyFeatures;
    limits?: PolicyLimits;
  } | null;
}

export interface ChildAutomationSettings {
  antiCheat: boolean;
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
}

export interface ChildAntiCheatingSettings {
  enabled: boolean;
  threshold: number | null;
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
}

export interface ChildImageGenerationSettings {
  enabled: boolean;
  maxPerHour: number | null;
  maxPerDay: number | null;
  systemEnabled?: boolean;
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
}

export interface ChildSettings {
  childId: string;
  dailyTimeLimitMinutes: number | null;
  timeLimitEnabled?: boolean;
  automations: ChildAutomationSettings;
  antiCheating: ChildAntiCheatingSettings;
  imageGeneration?: ChildImageGenerationSettings;
}

export interface ChildGeneratedImage {
  id: string;
  conversationId: string;
  messageId?: string | null;
  prompt: string;
  conversationTitle?: string | null;
  createdAt: string;
  downloadUrl: string;
}

export interface ParentGeneratedImage extends ChildGeneratedImage {
  childId?: string | null;
  childName: string;
}

export interface PolicyFeatures {
  antiCheating?: { available?: boolean; enabled?: boolean; threshold?: number };
  imageGeneration?: { available?: boolean };
  guidance?: { mandatory?: boolean };
  modelAccess?: string[];
  chatRetentionDays?: number;
  [key: string]: unknown;
}

export interface PolicyLimits {
  childrenMax?: number;
  imageGeneration?: { maxPerHour?: number; maxPerDay?: number };
  [key: string]: unknown;
}

export interface EffectivePolicy {
  features: PolicyFeatures;
  limits: PolicyLimits;
  defaults: Record<string, unknown>;
}

export interface NotificationItem {
  id: string;
  headline: string;
  body: string;
  category: UiFlagCategory | 'Usage' | 'Chat';
  createdAt: string;
}

export type AlertSortOrder = 'newest' | 'oldest';

export async function fetchSession(): Promise<AuthSession> {
  const client = getHttpClient();
  const { data } = await client.get<{ session: AuthSession }>('/auth/session');
  return data.session;
}

export async function fetchHouseholds(): Promise<HouseholdSummary[]> {
  const client = getHttpClient();
  const { data } = await client.get<HouseholdSummary[]>('/parent/households');
  return Array.isArray(data) ? data : [];
}

export async function fetchGuidanceQueue(): Promise<GuidanceItem[]> {
  const client = getHttpClient();
  const { data } = await client.get<GuidanceItem[]>('/parent/guidance');
  return Array.isArray(data) ? data : [];
}

export async function fetchNotifications(params?: { sort?: AlertSortOrder }): Promise<NotificationItem[]> {
  const client = getHttpClient();
  const { data } = await client.get<NotificationItem[]>('/parent/notifications', {
    params: {
      ...(params?.sort ? { sort: params.sort } : {}),
    },
  });
  return Array.isArray(data) ? data : [];
}

export async function fetchControlPreferences(): Promise<ControlPreferences> {
  const client = getHttpClient();
  const { data } = await client.get<ControlPreferences>('/parent/settings/preferences');
  return data;
}

export async function updateControlPreferences(preferences: ControlPreferences): Promise<ControlPreferences> {
  const client = getHttpClient();
  const { data } = await client.put<ControlPreferences>('/parent/settings/preferences', preferences);
  return data;
}

export async function fetchParentProfile(): Promise<ParentProfile> {
  const client = getHttpClient();
  const { data } = await client.get<ParentProfile>('/parent/me');
  return data;
}

export async function fetchEffectivePolicy(): Promise<EffectivePolicy> {
  const client = getHttpClient();
  const { data } = await client.get<EffectivePolicy>('/parent/policy');
  return data;
}

export interface UpdateParentProfileInput {
  firstName?: string;
  lastName?: string;
  contactNumber?: string | null;
  organization?: string | null;
}

export async function updateParentProfile(input: UpdateParentProfileInput): Promise<ParentProfile> {
  const client = getHttpClient();
  const { data } = await client.put<ParentProfile>('/parent', input);
  return data;
}

export async function fetchChildrenOverview(): Promise<ChildProfile[]> {
  const client = getHttpClient();
  const { data } = await client.get<{ children: ChildProfile[] } | ChildProfile[]>('/parent/children');
  if (Array.isArray(data)) {
    return data;
  }
  return data.children ?? [];
}

export async function fetchChildSessions(): Promise<ChildSession[]> {
  const client = getHttpClient();
  const { data } = await client.get<{ sessions: ChildSession[] }>('/parent/children/sessions');
  return data.sessions;
}

export async function fetchChildGeneratedImages(
  childId: string,
  params?: { limit?: number },
): Promise<ChildGeneratedImage[]> {
  const client = getHttpClient();
  const { data } = await client.get<{ items: ChildGeneratedImage[] }>(`/parent/children/${childId}/generated-images`, {
    params: {
      ...(params?.limit ? { limit: params.limit } : {}),
    },
  });
  return data.items ?? [];
}

export async function fetchGeneratedImages(params?: {
  childId?: string;
  limit?: number;
}): Promise<ParentGeneratedImage[]> {
  const client = getHttpClient();
  const { data } = await client.get<{ items: ParentGeneratedImage[] }>('/parent/generated-images', {
    params: {
      ...(params?.childId ? { childId: params.childId } : {}),
      ...(params?.limit ? { limit: params.limit } : {}),
    },
  });
  return data.items ?? [];
}

export interface SupportAttachmentInput {
  name: string;
  type: string;
  size: number;
  content?: string;
  s3Key?: string;
  url?: string;
}

export interface SupportRequestInput {
  subject: string;
  message: string;
  name?: string;
  email?: string;
  category?: string;
  attachments?: SupportAttachmentInput[];
}

export async function submitSupportRequest(input: SupportRequestInput): Promise<{ ticketId: string }> {
  const client = getHttpClient();
  const { data } = await client.post<{ ticketId: string }>('/parent/support', input);
  return data;
}

export async function presignSupportAttachmentUpload(params: {
  fileName: string;
  mimeType: string;
}): Promise<{ uploadUrl: string; s3Key: string; attachmentId: string }> {
  const client = getHttpClient();
  const { data } = await client.post<{ uploadUrl: string; s3Key: string; attachmentId: string }>(
    '/parent/support/attachments/presign',
    params,
  );
  return data;
}

export async function uploadFileToS3(uploadUrl: string, file: File, onProgress?: (progress: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', uploadUrl);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed (${request.status})`));
      }
    };
    request.onerror = () => reject(new Error('Upload failed'));
    request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    request.send(file);
  });
}

export interface OnboardingDraftRecord {
  parentId: string;
  payload: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export async function fetchOnboardingDraft(): Promise<OnboardingDraftRecord | null> {
  const client = getHttpClient();
  const { data } = await client.get<OnboardingDraftRecord | null>('/parent/onboarding/draft');
  return data;
}

export async function saveOnboardingDraft(payload: Record<string, unknown>): Promise<OnboardingDraftRecord> {
  const client = getHttpClient();
  const { data } = await client.put<OnboardingDraftRecord>('/parent/onboarding/draft', { payload });
  return data;
}

export async function clearOnboardingDraft(): Promise<{ cleared: boolean }> {
  const client = getHttpClient();
  const { data } = await client.delete<{ cleared: boolean }>('/parent/onboarding/draft');
  return data;
}

export interface CreateChildInput {
  nickname: string;
  firstName?: string;
  lastName?: string;
  age: number;
  grade?: number;
}

export async function createChildProfile(input: CreateChildInput): Promise<ChildProfile> {
  const client = getHttpClient();
  const { data } = await client.post<ChildProfile>('/parent/child', input);
  return data;
}

export interface UpdateChildProfileInput {
  nickname?: string;
  firstName?: string;
  lastName?: string;
  age?: number;
  grade?: number;
}

export async function updateChildProfile(childId: string, input: UpdateChildProfileInput): Promise<ChildProfile> {
  const client = getHttpClient();
  const { data } = await client.put<ChildProfile>(`/parent/child/${childId}`, input);
  return data;
}

export async function deleteChildProfile(childId: string): Promise<void> {
  const client = getHttpClient();
  await client.delete(`/parent/child/${childId}`);
}

export interface ChildAccessCodeResponse {
  childId: string;
  code: string;
  status?: 'AVAILABLE' | 'IN_USE';
}

export async function createChildAccessCode(childId: string): Promise<ChildAccessCodeResponse> {
  const client = getHttpClient();
  const { data } = await client.post<ChildAccessCodeResponse>(`/parent/child/${childId}/access-code`);
  return data;
}

export async function fetchChildAccessCode(childId: string): Promise<ChildAccessCodeResponse | null> {
  const client = getHttpClient();
  const { data } = await client.get<ChildAccessCodeResponse | null>(`/parent/child/${childId}/access-code`);
  return data;
}

export async function revokeChildAccessSessions(childId: string): Promise<{ childId: string; revokedSessions: number }> {
  const client = getHttpClient();
  const { data } = await client.post<{ childId: string; revokedSessions: number }>(
    `/parent/child/${childId}/access/revoke`
  );
  return data;
}

export async function fetchChildSettings(childId: string): Promise<ChildSettings> {
  const client = getHttpClient();
  const { data } = await client.get<ChildSettings>(`/parent/child/${childId}/settings`);
  return data;
}

export interface UpdateChildSettingsInput {
  dailyTimeLimitMinutes?: number | null;
  timeLimitEnabled?: boolean;
  automations?: Partial<Pick<ChildAutomationSettings, 'antiCheat'>>;
  antiCheating?: Partial<Pick<ChildAntiCheatingSettings, 'enabled' | 'threshold'>>;
  imageGeneration?: Partial<Pick<ChildImageGenerationSettings, 'enabled' | 'maxPerHour' | 'maxPerDay'>>;
}

export async function updateChildSettings(childId: string, input: UpdateChildSettingsInput): Promise<ChildSettings> {
  const client = getHttpClient();
  const { data } = await client.patch<ChildSettings>(`/parent/children/${childId}/settings`, input);
  return data;
}

export interface EffectiveChildPolicy {
  planFeatures: {
    timeLimitEnabled: boolean;
    antiCheating?: { available?: boolean; enabled?: boolean; threshold?: number };
    imageGeneration?: { available?: boolean };
  };
  planLimits?: PolicyLimits;
  childSettings: {
    dailyTimeLimitMinutes: number | null;
    timeLimitEnabled?: boolean;
    imageGeneration?: { enabled: boolean; maxPerHour: number | null; maxPerDay: number | null };
  };
  usageToday: { dateKey: string; usedSeconds: number; remainingSeconds: number | null };
  timeLimit: { enabledForParent: boolean; childEnabled?: boolean; configuredLimitMinutes: number | null; isUnlimited: boolean };
  imageGeneration?: {
    available: boolean;
    systemEnabled?: boolean;
    enabled: boolean;
    maxPerHour: number | null;
    maxPerDay: number | null;
    usage?: {
      usedLastHour: number;
      usedToday: number;
      remainingThisHour: number | null;
      remainingToday: number | null;
    };
  };
}

export async function fetchChildEffectivePolicy(childId: string): Promise<EffectiveChildPolicy> {
  const client = getHttpClient();
  const { data } = await client.get<EffectiveChildPolicy>(`/parent/children/${childId}/effective-policy`);
  return data;
}

export interface ChatSessionSummary {
  id: string;
  title?: string;
  startedAt: string | null;
  endedAt: string | null;
  lastActivityAt: string | null;
  isStarred: boolean;
  flagged: boolean;
}

export interface PaginatedResult<T> {
  items: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    sort?: string;
  };
}

export async function fetchChildChats(
  childId: string,
  params?: { limit?: number; offset?: number; sort?: 'recent' | 'oldest' | 'name'; search?: string }
): Promise<PaginatedResult<ChatSessionSummary>> {
  const client = getHttpClient();
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  if (params?.sort) query.set('sort', params.sort);
  if (params?.search) query.set('search', params.search);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const { data } = await client.get<PaginatedResult<ChatSessionSummary>>(
    `/parent/children/${childId}/chats${suffix}`
  );
  return data;
}

export interface ChatMessage {
  id: string;
  role: string;
  text: string;
  createdAt: string;
  decision?: string | null;
  flagged?: boolean;
  flagId?: string | null;
  flagCategory?: string | null;
  flagSource?: string | null;
  flagParentLabel?: 'CHEATING' | 'NOT_CHEATING' | null;
  flagSafetyLabel?: 'SAFETY' | 'NOT_SAFETY' | null;
  flagSafetyType?: 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT' | null;
  flagModCategories?: Record<string, unknown> | null;
  flagReviewNote?: string | null;
}

export async function fetchChatMessages(
  sessionId: string,
  params?: { limit?: number; offset?: number }
): Promise<PaginatedResult<ChatMessage>> {
  const client = getHttpClient();
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const { data } = await client.get<PaginatedResult<ChatMessage>>(
    `/parent/chats/${sessionId}/messages${suffix}`
  );
  return data;
}

export async function renameChatSession(sessionId: string, title: string): Promise<void> {
  const client = getHttpClient();
  await client.patch(`/parent/chats/${sessionId}/title`, { title });
}

export async function flagChatMessage(payload: {
  sessionId: string;
  messageId: string;
  childId: string;
  text: string;
  parentLabel?: 'CHEATING' | 'NOT_CHEATING';
  parentSafetyLabel?: 'SAFETY' | 'NOT_SAFETY';
  parentSafetyType?: 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT';
  modCategories?: Record<string, boolean>;
  tags?: string[];
  reason?: string;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  const client = getHttpClient();
  await client.post(`/parent/flags`, {
    childId: payload.childId,
    text: payload.text,
    sessionId: payload.sessionId,
    messageId: payload.messageId,
    parentLabel: payload.parentLabel,
    parentSafetyLabel: payload.parentSafetyLabel,
    parentSafetyType: payload.parentSafetyType,
    modCategories: payload.modCategories,
    tags: payload.tags,
    reason: payload.reason ?? 'parent_flag',
    metadata: payload.metadata,
    source: 'parent',
  });
}

export async function deleteChatSession(sessionId: string): Promise<void> {
  const client = getHttpClient();
  await client.delete(`/parent/chats/${sessionId}`);
}

export async function fetchFlags(params?: {
  childId?: string;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  limit?: number;
  sort?: AlertSortOrder;
}): Promise<FlagRecord[]> {
  const client = getHttpClient();
  const { data } = await client.get<FlagRecord[]>('/parent/flags', {
    params: {
      ...(params?.childId ? { childId: params.childId } : {}),
      ...(params?.status ? { status: params.status } : {}),
      ...(params?.limit ? { limit: params.limit } : {}),
      ...(params?.sort ? { sort: params.sort } : {}),
    },
  });
  return data;
}

export async function resolveFlag(flagId: string, reviewStatus: 'APPROVED' | 'REJECTED'): Promise<{ id: string }> {
  const client = getHttpClient();
  const { data } = await client.patch<{ id: string }>(`/parent/flags/${flagId}`, { reviewStatus });
  return data;
}

export interface StarredPrompt {
  id: string;
  sessionId: string;
  childId: string;
  childName: string;
  text: string;
  createdAt: string;
  starredAt?: string | null;
}

export async function fetchStarredPrompts(params?: { childId?: string; limit?: number }): Promise<StarredPrompt[]> {
  const client = getHttpClient();
  const { data } = await client.get<StarredPrompt[]>('/parent/chats/starred', {
    params: {
      ...(params?.childId ? { childId: params.childId } : {}),
      ...(params?.limit ? { limit: params.limit } : {}),
    },
  });
  return Array.isArray(data) ? data : [];
}

export interface ChildSession {
  id: string;
  childId: string;
  childName: string;
  topic?: string | null;
  isStarred?: boolean;
  flagged: boolean;
  durationMinutes?: number | null;
  startedAt?: string;
}

export interface ReflectionPrompt {
  id: string;
  childId: string;
  parentId: string;
  message: string;
  createdAt: string;
}

export async function createReflectionPrompt(childId: string, message: string): Promise<ReflectionPrompt> {
  const client = getHttpClient();
  const { data } = await client.post<ReflectionPrompt>(`/children/${childId}/reflections`, { message });
  return data;
}

export async function fetchReflectionPrompts(childId: string): Promise<ReflectionPrompt[]> {
  const client = getHttpClient();
  const { data } = await client.get<ReflectionPrompt[]>(`/children/${childId}/reflections`);
  return data;
}

export interface UsageEvent {
  id: string;
  parentId: string;
  childId: string;
  type: 'ALLOW' | 'BLOCK' | 'GUIDANCE' | 'FLAGGED';
  prompt: string;
  model?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  sessionId?: string | null;
  child?: { id: string; nickname: string; firstName?: string | null; lastName?: string | null; displayName?: string };
  session?: { id: string; title?: string | null };
}

export function getChildDisplayName(child?: {
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
} | null): string {
  return (
    child?.displayName?.trim() ||
    child?.nickname?.trim() ||
    `${child?.firstName ?? ''} ${child?.lastName ?? ''}`.trim() ||
    'Learner'
  );
}

export async function fetchUsageEvents(params?: { limit?: number; childId?: string }): Promise<UsageEvent[]> {
  const client = getHttpClient();
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.childId) query.set('childId', params.childId);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const { data } = await client.get<UsageEvent[]>(`/parent/usage${suffix}`);
  return data;
}
